import { useTranslation } from 'react-i18next';
import { TbBrandWhatsapp, TbChecks, TbMail, TbMapPin, TbPhoneCall } from 'react-icons/tb';

import BrandMark from 'src/components/brandMark/BrandMark';
import { DEFAULT_EMAIL } from 'src/constants/vars';

/** Placeholder text line inside a chat bubble */
const Line = ({ w, light = false }: { w: string; light?: boolean }) => (
    <span className={`block h-1.5 rounded-full ${light ? "bg-white/70" : "bg-primary-200"}`} style={{ width: w }} />
);

/**
 * Illustrated contact scene: a phone showing a live chat with the company,
 * surrounded by floating contact chips.
 */
const ContactVisual = () => {
    const { t } = useTranslation("common");

    return (
        <div
            role="img"
            aria-label={t("contact.image_alt", { ns: "home" })}
            className="group relative isolate aspect-[3/4] overflow-hidden min-[420px]:aspect-square rounded-3xl bg-gradient-to-br from-primary-100 via-primary-50 to-white shadow-card-hover ring-1 ring-primary-100"
        >
            {/* texture, glow & rings */}
            <div aria-hidden className="bg-dots absolute inset-0 -z-10 text-primary-300/30" />
            <div aria-hidden className="absolute left-1/2 top-1/2 -z-10 h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-300/40 blur-3xl" />
            <div aria-hidden className="absolute left-1/2 top-1/2 -z-10 aspect-square w-[92%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-200/60" />
            <div aria-hidden className="absolute left-1/2 top-1/2 -z-10 aspect-square w-[70%] -translate-x-1/2 -translate-y-1/2 animate-[spin_50s_linear_infinite] rounded-full border border-dashed border-primary-300/70" />

            {/* Phone mockup */}
            <div className="absolute left-1/2 top-1/2 w-[48%] -translate-x-1/2 -translate-y-1/2 sm:w-[44%]">
                <div className="animate-float rounded-[1.75rem] bg-heading p-1.5 shadow-[0_30px_60px_-20px_rgba(14,60,98,.55)] sm:rounded-[2rem] sm:p-2">
                    <div className="relative overflow-hidden rounded-[1.4rem] bg-gradient-to-b from-primary-10 to-white sm:rounded-[1.6rem]">
                        {/* notch */}
                        <div aria-hidden className="absolute left-1/2 top-1.5 h-1.5 w-10 -translate-x-1/2 rounded-full bg-heading/90 sm:w-14" />

                        {/* chat header */}
                        <div className="flex items-center gap-2 bg-gradient-to-r from-primary-400 to-dark-primary-500 px-3 pb-2.5 pt-5 sm:gap-2.5 sm:pb-3 sm:pt-7">
                            <BrandMark className="h-6 w-6 shrink-0 rounded-lg ring-2 ring-white/40 sm:h-8 sm:w-8" />
                            <div className="min-w-0 flex-1 space-y-1">
                                <span className="block h-1.5 w-3/4 rounded-full bg-white/90 sm:h-2" />
                                <span className="flex items-center gap-1">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                                    <span className="block h-1 w-1/3 rounded-full bg-white/60" />
                                </span>
                            </div>
                            <TbPhoneCall className="h-4 w-4 shrink-0 text-white/90 sm:h-5 sm:w-5" />
                        </div>

                        {/* conversation */}
                        <div className="space-y-2 px-2.5 py-3 sm:space-y-2.5 sm:px-3 sm:py-4">
                            <div className="w-[70%] space-y-1.5 rounded-2xl rounded-ss-md bg-white p-2 shadow-sm ring-1 ring-primary-50 sm:p-2.5">
                                <Line w="90%" /><Line w="60%" />
                            </div>
                            <div className="ms-auto w-[65%] space-y-1.5 rounded-2xl rounded-se-md bg-gradient-to-br from-primary-400 to-dark-primary-500 p-2 sm:p-2.5">
                                <Line w="95%" light /><Line w="70%" light />
                                <TbChecks className="ms-auto h-3 w-3 text-white/80" />
                            </div>
                            <div className="hidden w-[55%] space-y-1.5 rounded-2xl rounded-ss-md bg-white p-2 shadow-sm ring-1 ring-primary-50 min-[420px]:block sm:p-2.5">
                                <Line w="85%" />
                            </div>
                            {/* typing indicator */}
                            <div className="ms-auto flex w-fit items-center gap-1 rounded-full bg-primary-100 px-3 py-2">
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500" />
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:.15s]" />
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:.3s]" />
                            </div>
                        </div>

                        {/* input bar */}
                        <div className="flex items-center gap-2 border-t border-primary-50 px-2.5 py-2 sm:px-3 sm:py-2.5">
                            <span className="h-5 flex-1 rounded-full bg-gray-100 sm:h-6" />
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-primary-400 text-white sm:h-6 sm:w-6">
                                <TbBrandWhatsapp className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating contact chips */}
            <div className="absolute start-[3%] top-[30%] flex animate-float items-center gap-2 rounded-2xl bg-white/90 p-1.5 pe-3 shadow-card ring-1 ring-primary-100 backdrop-blur [animation-delay:.8s] sm:gap-2.5 sm:p-2 sm:pe-4">
                <span className="grid h-7 w-7 place-items-center rounded-xl bg-primary-50 text-primary-500 sm:h-9 sm:w-9">
                    <TbPhoneCall className="h-4 w-4 sm:h-5 sm:w-5" />
                </span>
                <span className="text-[10px] font-normal tracking-brand text-heading sm:text-xs">{t("footer.phone1")}</span>
            </div>

            <div className="absolute bottom-[7%] end-[3%] flex animate-float items-center gap-2 rounded-2xl bg-white/90 p-1.5 pe-3 shadow-card ring-1 ring-primary-100 backdrop-blur [animation-delay:2s] sm:gap-2.5 sm:p-2 sm:pe-4">
                <span className="grid h-7 w-7 place-items-center rounded-xl bg-primary-50 text-primary-500 sm:h-9 sm:w-9">
                    <TbMail className="h-4 w-4 sm:h-5 sm:w-5" />
                </span>
                <span className="text-[10px] font-normal tracking-brand text-heading sm:text-xs">{DEFAULT_EMAIL}</span>
            </div>

            <div className="absolute end-[7%] top-[9%] grid h-10 w-10 animate-float place-items-center rounded-full bg-gradient-to-br from-primary-400 to-dark-primary-500 text-white shadow-glow [animation-delay:1.4s] sm:h-12 sm:w-12">
                <TbBrandWhatsapp className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>

            <div className="absolute bottom-[16%] start-[7%] grid h-9 w-9 animate-float place-items-center rounded-full bg-white text-dark-primary-500 shadow-card ring-1 ring-primary-100 [animation-delay:2.6s] sm:h-11 sm:w-11">
                <TbMapPin className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
        </div>
    );
};

export default ContactVisual;
